import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  FlaskConical, Play, Square, AlertTriangle, Radio, Clock, Download,
  Loader2, Wifi, WifiOff, CheckCircle, XCircle, Target, Zap,
  Volume2, VolumeX, Maximize, Minimize, ArrowRight, RotateCcw, Pause, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Area, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

/*
 * PHASES:
 * start        → sensor + plan + recommendation
 * preflight    → auto checks
 * ready        → instructions, "JAG ÄR REDO"
 * baseline     → measuring, stability check
 * event_prep   → distance/position instructions, "JAG ÄR PÅ PLATS"
 * countdown    → 3-2-1
 * event_active → "VEJPA NU" / "SPRAYA NU" (auto marker)
 * post_event   → measuring response, showing peaks
 * recovery     → waiting for levels to return
 * completing   → auto-stop
 * ground_truth → confirm what happened
 * results      → full analysis
 * next_test    → recommendation + room check
 * room_check   → waiting for room to be ready
 */

const EVENT_LABELS = {
  vape_now: 'VEJPA NU',
  spray_now: 'SPRAYA NU',
  steam_now: 'PRODUCERA ÅNGA NU',
  simultaneous: 'VEJPA + SPRAYA NU',
};

const CATEGORY_LABELS = {
  normal: 'Normal kontroll',
  vape: 'Vape',
  spray: 'Spray',
  mixed: 'Blandat',
  steam: 'Ånga',
};

const GT_OPTIONS = [
  { value: 'vape', label: 'VAPE', color: '#ef4444' },
  { value: 'spray', label: 'SPRAY', color: '#f59e0b' },
  { value: 'normal', label: 'INGET HÄNDE', color: '#22c55e' },
  { value: 'mixed', label: 'BLANDAT', color: '#a855f7' },
  { value: 'steam', label: 'ÅNGA', color: '#3b82f6' },
  { value: 'invalid', label: 'TESTET BLEV FEL', color: '#94a3b8' },
];

export default function GuidedTestWizard({ devices, onExit, onComplete }) {
  const { token } = useAuth();
  const { subscribeToDevice, unsubscribeFromDevice } = useWebSocket();
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Core state
  const [phase, setPhase] = useState('start');
  const [deviceId, setDeviceId] = useState('');
  const [planId] = useState('vape-spray-validation');
  const [recommendation, setRecommendation] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [session, setSession] = useState(null);
  const [spec, setSpec] = useState(null);
  const [protocol, setProtocol] = useState(null);

  // Live data
  const [liveStatus, setLiveStatus] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [baselineCheck, setBaselineCheck] = useState(null);
  const [recoveryCheck, setRecoveryCheck] = useState(null);
  const [roomStatus, setRoomStatus] = useState(null);

  // UI state
  const [countdown, setCountdown] = useState(null);
  const [eventElapsed, setEventElapsed] = useState(0);
  const [baselineTimer, setBaselineTimer] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [selectedGT, setSelectedGT] = useState(null);

  // Mixed test state
  const [sequenceIndex, setSequenceIndex] = useState(0);

  // Refs for intervals
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const audioCtx = useRef(null);

  // ===== AUDIO =====
  const playBeep = useCallback((freq = 800, duration = 150) => {
    if (!soundEnabled) return;
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + duration / 1000);
    } catch { /* ignore audio errors */ }
  }, [soundEnabled]);

  // ===== LOAD RECOMMENDATION =====
  const loadRecommendation = useCallback(async (devId) => {
    if (!devId) return;
    const [recR, covR] = await Promise.all([
      fetch(`${API}/api/test-lab/plans/${planId}/recommend?device_id=${devId}`, { headers: h }),
      fetch(`${API}/api/test-lab/plans/${planId}/coverage?device_id=${devId}`, { headers: h }),
    ]);
    if (recR.ok) setRecommendation(await recR.json());
    if (covR.ok) setCoverage(await covR.json());
  }, [token, planId]);

  // Auto-select first device with readings
  useEffect(() => {
    if (devices?.length > 0 && !deviceId) {
      const withName = devices.find(d => d.name && !d.name.startsWith('TEST_'));
      setDeviceId(withName?.id || devices[0].id);
    }
  }, [devices]);

  useEffect(() => {
    if (deviceId) loadRecommendation(deviceId);
  }, [deviceId, loadRecommendation]);

  // ===== POLLING =====
  useEffect(() => {
    if (!session?.id || phase === 'start' || phase === 'results' || phase === 'next_test') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/test-lab/sessions/${session.id}/live-status`, { headers: h });
        if (r.ok) {
          const d = await r.json();
          setLiveStatus(d);
          if (d.latest_reading) {
            setChartData(prev => {
              const ts = d.latest_reading.timestamp?.slice(11, 19) || '';
              if (prev.length > 0 && prev[prev.length - 1].time === ts) return prev;
              return [...prev, {
                time: ts, PM1: d.latest_reading.pm1, 'PM2.5': d.latest_reading.pm2_5,
                PM10: d.latest_reading.pm10, VOC: d.latest_reading.voc,
              }].slice(-300);
            });
          }
        }
        // Phase-specific checks
        if (phase === 'baseline') {
          const br = await fetch(`${API}/api/test-lab/guided/${session.id}/baseline-check`, { headers: h });
          if (br.ok) setBaselineCheck(await br.json());
        }
        if (phase === 'post_event' || phase === 'recovery') {
          const rr = await fetch(`${API}/api/test-lab/guided/${session.id}/recovery-check`, { headers: h });
          if (rr.ok) {
            const data = await rr.json();
            setRecoveryCheck(data);
            if (data.should_stop && phase === 'recovery') {
              autoComplete();
            }
          }
        }
      } catch { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [session?.id, phase, token]);

  // WebSocket subscription
  useEffect(() => {
    if (!session?.device_id) return;
    subscribeToDevice(session.device_id);
    return () => unsubscribeFromDevice(session.device_id);
  }, [session?.device_id]);

  // Baseline timer
  useEffect(() => {
    if (phase !== 'baseline') { if (timerRef.current) clearInterval(timerRef.current); return; }
    setBaselineTimer(0);
    timerRef.current = setInterval(() => setBaselineTimer(t => t + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [phase]);

  // Post-event timer
  useEffect(() => {
    if (phase !== 'post_event' && phase !== 'recovery') { return; }
    const iv = setInterval(() => setEventElapsed(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, [phase]);

  // Auto-advance baseline when stable and enough time
  useEffect(() => {
    if (phase !== 'baseline') return;
    const minTime = Math.min(protocol?.baseline_seconds || 60, 30);
    if (baselineCheck?.stable && baselineTimer >= minTime) {
      baselineReady();
    }
  }, [phase, baselineCheck?.stable, baselineTimer]);

  // ===== ACTIONS =====
  const startGuided = async (overrideSpec = null) => {
    const useSpec = overrideSpec || recommendation?.spec;
    if (!useSpec || !deviceId) return;
    setSpec(useSpec);

    const r = await fetch(`${API}/api/test-lab/guided/start`, {
      method: 'POST', headers: h,
      body: JSON.stringify({ device_id: deviceId, plan_id: planId, spec_id: useSpec.id }),
    });
    if (!r.ok) { toast.error('Kunde inte starta session'); return; }
    const s = await r.json();
    setSession(s);
    setProtocol(s.guided_protocol);
    setChartData([]);
    setSequenceIndex(0);
    setPhase('preflight');

    // Auto-advance preflight after a brief check
    setTimeout(() => setPhase('ready'), 2000);
  };

  const startBaseline = () => {
    setPhase('baseline');
  };

  const baselineReady = () => {
    if (spec?.category === 'normal') {
      // Normal control: no event, just record for duration then complete
      setPhase('post_event');
      setEventElapsed(0);
      // Auto-create a marker for normal observation start
      fetch(`${API}/api/test-lab/sessions/${session.id}/marker`, {
        method: 'POST', headers: h,
        body: JSON.stringify({ marker_type: 'note', note: 'Normal kontroll - observation startad' }),
      });
      return;
    }
    setPhase('event_prep');
  };

  const startCountdown = () => {
    setPhase('countdown');
    setCountdown(3);
  };

  // Countdown logic
  useEffect(() => {
    if (phase !== 'countdown' || countdown === null) return;
    if (countdown > 0) {
      playBeep(600, 100);
      const t = setTimeout(() => setCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
    // Countdown reached 0 — fire event
    playBeep(1200, 400);
    setPhase('event_active');

    // Auto-create event marker
    const sequence = spec?.sequence;
    let markerType = spec?.event_type || 'vape_now';
    if (sequence && sequence[sequenceIndex]) {
      markerType = sequence[sequenceIndex];
    }
    fetch(`${API}/api/test-lab/sessions/${session.id}/marker`, {
      method: 'POST', headers: h,
      body: JSON.stringify({ marker_type: markerType, note: `Guidat test - auto (steg ${sequenceIndex + 1})` }),
    });

    // After a few seconds, transition to post_event or next sequence step
    setTimeout(() => {
      const seq = spec?.sequence;
      if (seq && sequenceIndex < seq.length - 1 && seq[sequenceIndex] !== 'simultaneous') {
        // More events in sequence — wait interval then next prep
        setSequenceIndex(i => i + 1);
        setPhase('event_wait');
      } else {
        setPhase('post_event');
        setEventElapsed(0);
      }
    }, 5000);
  }, [phase, countdown]);

  // Event wait (between mixed test events)
  useEffect(() => {
    if (phase !== 'event_wait') return;
    const interval = protocol?.event_interval_seconds || 30;
    const t = setTimeout(() => setPhase('event_prep'), interval * 1000);
    return () => clearTimeout(t);
  }, [phase]);

  // Auto-transition from post_event to recovery after min time
  useEffect(() => {
    if (phase !== 'post_event') return;
    const minPost = protocol?.min_post_event_seconds || 120;
    const t = setTimeout(() => setPhase('recovery'), minPost * 1000);
    return () => clearTimeout(t);
  }, [phase]);

  const autoComplete = async () => {
    setPhase('completing');
    const r = await fetch(`${API}/api/test-lab/guided/${session.id}/complete`, { method: 'POST', headers: h });
    if (r.ok) {
      const data = await r.json();
      setCompletionData(data);
      // Pre-select ground truth based on test spec
      if (spec?.category === 'normal') setSelectedGT('normal');
      else if (spec?.category === 'vape') setSelectedGT('vape');
      else if (spec?.category === 'spray') setSelectedGT('spray');
      else if (spec?.category === 'mixed') setSelectedGT('mixed');
      else if (spec?.category === 'steam') setSelectedGT('steam');
      setPhase('ground_truth');
    } else {
      toast.error('Kunde inte avsluta session');
      setPhase('post_event');
    }
  };

  const confirmGroundTruth = async () => {
    if (!selectedGT) return;
    await fetch(`${API}/api/test-lab/sessions/${session.id}/ground-truth`, {
      method: 'PUT', headers: h,
      body: JSON.stringify({ label: selectedGT, confidence: selectedGT === 'invalid' ? 'uncertain' : 'confirmed' }),
    });
    setPhase('results');
  };

  const goToNextTest = async () => {
    await loadRecommendation(deviceId);
    setSession(null);
    setChartData([]);
    setLiveStatus(null);
    setBaselineCheck(null);
    setRecoveryCheck(null);
    setCompletionData(null);
    setSelectedGT(null);
    setPhase('next_test');
    // Check room readiness
    const r = await fetch(`${API}/api/test-lab/room-status/${deviceId}`, { headers: h });
    if (r.ok) setRoomStatus(await r.json());
  };

  const forceStop = async () => {
    if (!session?.id) return;
    if (!window.confirm('Avsluta testet manuellt?')) return;
    await autoComplete();
  };

  // ===== HELPER: format seconds =====
  const fmtTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  // ===== MINI LIVE VALUES =====
  const LiveValues = ({ latest }) => {
    if (!latest) return <div className="text-xs text-muted-foreground text-center py-4">Vantar pa sensordata...</div>;
    return (
      <div className="grid grid-cols-3 gap-2 text-center">
        {[['PM1', latest.pm1, '#3b82f6'], ['PM2.5', latest.pm2_5, '#f59e0b'], ['PM10', latest.pm10, '#ef4444']].map(([l, v, c]) => (
          <div key={l} className="rounded-lg border bg-card p-2">
            <div className="text-[9px] text-muted-foreground uppercase">{l}</div>
            <div className="text-xl font-bold font-mono" style={{ color: c }}>{v != null ? v : '—'}</div>
          </div>
        ))}
      </div>
    );
  };

  // ===== MINI CHART =====
  const MiniChart = ({ data, markers = [], height = 200 }) => {
    if (data.length < 2) return null;
    return (
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
          <XAxis dataKey="time" tick={{ fontSize: 8 }} />
          <YAxis tick={{ fontSize: 9 }} />
          <Tooltip contentStyle={{ fontSize: 10 }} />
          <Line type="monotone" dataKey="PM1" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
          <Line type="monotone" dataKey="PM2.5" stroke="#f59e0b" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="PM10" stroke="#ef4444" strokeWidth={1.5} dot={false} />
          {markers.map((m, i) => (
            <ReferenceLine key={i} x={m.timestamp?.slice(11, 19)} stroke="#ef4444" strokeDasharray="3 3" strokeWidth={2} />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    );
  };

  const containerCls = fullscreen
    ? 'fixed inset-0 z-50 bg-background p-4 overflow-y-auto flex flex-col'
    : 'space-y-4';

  // ================= RENDER BY PHASE =================

  // ----- START -----
  if (phase === 'start') {
    const rec = recommendation;
    const dev = devices?.find(d => d.id === deviceId);
    return (
      <div className={containerCls} data-testid="guided-start">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold tracking-tight">Guidat test</h2>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSoundEnabled(!soundEnabled)}>
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={onExit}>Tillbaka</Button>
          </div>
        </div>

        {/* Sensor + status */}
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Sensor</div>
                <select className="mt-1 text-sm font-semibold bg-transparent border rounded px-2 py-1"
                  value={deviceId} onChange={e => setDeviceId(e.target.value)} data-testid="guided-sensor-select">
                  {devices?.map(d => <option key={d.id} value={d.id}>{d.name || d.device_eui}</option>)}
                </select>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Testprotokoll</div>
                <div className="text-sm font-semibold">Vape/Spray Validation</div>
              </div>
            </div>
            {coverage && (
              <div>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-mono font-semibold">{coverage.total_completed} / {coverage.total_target}</span>
                </div>
                <Progress value={coverage.progress_pct} className="h-2" />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recommendation */}
        {rec && !rec.done && (
          <Card className="border-2 border-blue-200 bg-blue-50/30 dark:bg-blue-950/10">
            <CardContent className="pt-4 space-y-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Rekommenderat nasta test</div>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                  style={{ backgroundColor: rec.spec.category === 'vape' ? '#ef4444' : rec.spec.category === 'spray' ? '#f59e0b' : rec.spec.category === 'mixed' ? '#a855f7' : '#22c55e' }}>
                  {rec.spec.category === 'vape' ? 'V' : rec.spec.category === 'spray' ? 'S' : rec.spec.category === 'mixed' ? 'M' : 'N'}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-lg">{rec.spec.label}</div>
                  <div className="text-sm text-muted-foreground">
                    {rec.spec.distance_m && `Avstand: ${rec.spec.distance_m}m`}
                    {rec.spec.ventilation && ` | Ventilation: ${rec.spec.ventilation}`}
                    {rec.spec.door && ` | Dorr: ${rec.spec.door === 'closed' ? 'Stangd' : 'Oppen'}`}
                  </div>
                </div>
              </div>
              <div className="text-sm text-muted-foreground italic">{rec.reason}</div>
              <Button className="w-full h-12 text-base font-bold" onClick={() => startGuided()} data-testid="start-guided-btn">
                <Play className="h-5 w-5 mr-2" /> STARTA GUIDAT TEST
              </Button>
            </CardContent>
          </Card>
        )}

        {rec?.done && (
          <Card className="border-2 border-emerald-200 bg-emerald-50/30 p-6 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-emerald-500 mb-3" />
            <div className="font-bold text-lg">Alla tester genomforda!</div>
            <div className="text-sm text-muted-foreground mt-1">{rec.message}</div>
          </Card>
        )}

        {/* Coverage summary */}
        {coverage && coverage.categories && (
          <Card>
            <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Dataset-tackning</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {Object.entries(coverage.categories).map(([cat, data]) => (
                <div key={cat} className="flex items-center gap-3">
                  <span className="text-xs w-16 text-muted-foreground capitalize">{CATEGORY_LABELS[cat] || cat}</span>
                  <div className="flex-1"><Progress value={data.target > 0 ? data.completed / data.target * 100 : 0} className="h-2" /></div>
                  <span className="text-xs font-mono w-12 text-right">{data.completed}/{data.target}</span>
                </div>
              ))}
              {coverage.gaps?.length > 0 && (
                <div className="mt-2 pt-2 border-t text-xs text-muted-foreground">
                  <span className="font-semibold">Luckor: </span>
                  {coverage.gaps.slice(0, 5).map(g => g.label).join(', ')}
                  {coverage.gaps.length > 5 && ` +${coverage.gaps.length - 5} till`}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ----- PREFLIGHT -----
  if (phase === 'preflight') {
    return (
      <div className={containerCls + ' flex items-center justify-center'} data-testid="guided-preflight" style={{ minHeight: fullscreen ? undefined : 400 }}>
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-blue-500" />
          <h2 className="text-xl font-bold">FORBEREDELSER</h2>
          <div className="space-y-2 text-left max-w-xs mx-auto">
            {['Sensor online', 'Datastream ansluten', 'PM1 tillganglig', 'PM2.5 tillganglig', 'PM10 tillganglig'].map((check, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" style={{ animationDelay: `${i * 300}ms` }}>
                <CheckCircle className="h-4 w-4 text-emerald-500" />
                <span>{check}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ----- READY (instructions) -----
  if (phase === 'ready') {
    const isNormal = spec?.category === 'normal';
    return (
      <div className={containerCls} data-testid="guided-ready">
        <div className="text-center space-y-6 py-8">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Forberedelser</div>
          <h2 className="text-2xl font-bold">{isNormal ? 'NORMAL KONTROLL' : spec?.label}</h2>

          {!isNormal && (
            <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
              {spec?.distance_m && <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">AVSTAND</div><div className="text-2xl font-bold">{spec.distance_m}m</div></div>}
              <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">VENTILATION</div><div className="text-lg font-bold capitalize">{spec?.ventilation === 'normal' ? 'Normal' : spec?.ventilation}</div></div>
              <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">DORR</div><div className="text-lg font-bold">{spec?.door === 'closed' ? 'Stangd' : 'Oppen'}</div></div>
            </div>
          )}

          <Card className="max-w-md mx-auto text-left">
            <CardContent className="pt-4 space-y-2 text-sm">
              {isNormal ? (
                <>
                  <p>Se till att ingen vejpar eller sprayar i rummet.</p>
                  <p>Gor ingenting ovanligt under testet.</p>
                </>
              ) : (
                <>
                  <p>Se till att ingen vejpar eller sprayar i rummet.</p>
                  {spec?.distance_m && <p className="font-semibold">Placera dig cirka {spec.distance_m} meter fran sensorn.</p>}
                  {spec?.door === 'closed' && <p>Stang dorren.</p>}
                  {spec?.instruction && <p className="italic text-muted-foreground">{spec.instruction}</p>}
                </>
              )}
            </CardContent>
          </Card>

          <Button className="h-14 px-12 text-lg font-bold" onClick={startBaseline} data-testid="ready-btn">
            JAG AR REDO
          </Button>
        </div>
      </div>
    );
  }

  // ----- BASELINE -----
  if (phase === 'baseline') {
    const targetSec = protocol?.baseline_seconds || 60;
    const maxExtend = protocol?.baseline_max_extend || 30;
    const isOverTime = baselineTimer > targetSec;
    const isStable = baselineCheck?.stable;

    return (
      <div className={containerCls} data-testid="guided-baseline">
        <div className="text-center space-y-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Steg 1 av 4</div>
          <h2 className="text-2xl font-bold">MATER BASELINE</h2>

          <div className="text-5xl font-bold font-mono tabular-nums">
            {fmtTime(Math.max(0, targetSec - baselineTimer))}
          </div>

          <Card className="max-w-md mx-auto">
            <CardContent className="pt-4 text-sm text-center space-y-2">
              <p className="font-semibold">Gor ingenting just nu.</p>
              <p className="text-muted-foreground">Undvik vape, spray, oppna/stanga dorren och onodig rorelse nara sensorn.</p>
            </CardContent>
          </Card>

          {isOverTime && !isStable && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 max-w-md mx-auto">
              <AlertTriangle className="h-5 w-5 text-amber-600 mx-auto mb-1" />
              <p className="text-sm font-semibold text-amber-700">Baseline ar inte stabil</p>
              <p className="text-xs text-amber-600">Partikelnivåerna forandras fortfarande. Vantar ytterligare {maxExtend}s.</p>
            </div>
          )}

          {baselineCheck && (
            <div className="text-xs text-muted-foreground">
              Stabilitet: stdev={baselineCheck.details?.stdev_pm25} (max {baselineCheck.details?.threshold}) | {baselineCheck.readings_count} matpunkter
            </div>
          )}

          <LiveValues latest={liveStatus?.latest_reading} />
          <MiniChart data={chartData} height={160} />

          {/* Manual override */}
          {isOverTime && (
            <Button variant="outline" size="sm" onClick={baselineReady}>
              Fortsatt anda (baseline kanske inte stabil)
            </Button>
          )}
        </div>
      </div>
    );
  }

  // ----- EVENT PREP -----
  if (phase === 'event_prep') {
    const seq = spec?.sequence;
    const seqLabels = spec?.sequence_labels;
    const isMulti = seq && seq.length > 1;
    const currentLabel = seqLabels ? seqLabels[sequenceIndex] : EVENT_LABELS[spec?.event_type] || 'UTFOR HANDELSEN';

    return (
      <div className={containerCls} data-testid="guided-event-prep">
        <div className="text-center space-y-6 py-8">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Steg 2 av 4{isMulti ? ` — Handelse ${sequenceIndex + 1}/${seq.length}` : ''}</div>
          <h2 className="text-2xl font-bold">FORBERED {spec?.category?.toUpperCase()}-TEST</h2>

          <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
            {spec?.distance_m && <div className="rounded-lg border-2 border-blue-300 p-3 text-center"><div className="text-[10px] text-muted-foreground">AVSTAND</div><div className="text-3xl font-bold text-blue-600">{spec.distance_m}m</div></div>}
            <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">VENTILATION</div><div className="text-base font-bold capitalize">{spec?.ventilation || 'Normal'}</div></div>
            <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">DORR</div><div className="text-base font-bold">{spec?.door === 'closed' ? 'Stangd' : 'Oppen'}</div></div>
          </div>

          <Card className="max-w-md mx-auto">
            <CardContent className="pt-4 space-y-2 text-sm text-center">
              <p>Stall dig cirka <strong>{spec?.distance_m || 1} meter</strong> fran sensorn.</p>
              <p>Forbered {spec?.category === 'vape' ? 'vape-enheten' : spec?.category === 'spray' ? 'sprayen' : 'testutrustningen'}.</p>
              <p className="font-semibold text-muted-foreground">Vanta tills nedrakningen når 0.</p>
            </CardContent>
          </Card>

          <Button className="h-14 px-12 text-lg font-bold" onClick={startCountdown} data-testid="in-position-btn">
            JAG AR PA PLATS
          </Button>
        </div>
      </div>
    );
  }

  // ----- COUNTDOWN -----
  if (phase === 'countdown') {
    return (
      <div className={containerCls + ' flex items-center justify-center'} data-testid="guided-countdown"
        style={{ minHeight: fullscreen ? undefined : 500 }}>
        <div className="text-center">
          <div className="text-xs text-muted-foreground uppercase tracking-widest mb-4">UTFOR INTE TESTET AN</div>
          <div className="text-[120px] font-black leading-none tabular-nums" style={{ color: countdown > 0 ? '#f59e0b' : '#ef4444' }}>
            {countdown > 0 ? countdown : '!'}
          </div>
        </div>
      </div>
    );
  }

  // ----- EVENT ACTIVE -----
  if (phase === 'event_active') {
    const seq = spec?.sequence;
    const seqLabels = spec?.sequence_labels;
    const label = seqLabels ? seqLabels[sequenceIndex] : EVENT_LABELS[spec?.event_type] || 'UTFOR HANDELSEN';

    return (
      <div className={containerCls + ' flex items-center justify-center'} data-testid="guided-event-active"
        style={{ minHeight: fullscreen ? undefined : 500, backgroundColor: '#ef444422' }}>
        <div className="text-center animate-pulse">
          <div className="text-[64px] font-black leading-tight text-red-600">{label}</div>
          <div className="mt-4 text-lg text-muted-foreground">Handelsemorkering skapad automatiskt</div>
        </div>
      </div>
    );
  }

  // ----- EVENT WAIT (between mixed events) -----
  if (phase === 'event_wait') {
    const interval = protocol?.event_interval_seconds || 30;
    return (
      <div className={containerCls + ' flex items-center justify-center'} data-testid="guided-event-wait">
        <div className="text-center space-y-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Vantar pa nasta handelse</div>
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-amber-500" />
          <p className="text-sm">Nasta handelse startar om {interval}s</p>
          <p className="text-sm text-muted-foreground">Forbered {spec?.sequence_labels?.[sequenceIndex] || 'nasta steg'}</p>
        </div>
      </div>
    );
  }

  // ----- POST EVENT -----
  if (phase === 'post_event') {
    const isNormal = spec?.category === 'normal';
    const peakPm25 = recoveryCheck?.peak_pm25 || Math.max(...chartData.map(d => d['PM2.5'] || 0), 0);
    const blPm25 = baselineCheck?.baseline?.pm2_5 || 0;
    const delta = peakPm25 - blPm25;
    const minPost = protocol?.min_post_event_seconds || 120;

    return (
      <div className={containerCls} data-testid="guided-post-event">
        <div className="text-center space-y-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Steg 3 av 4</div>
          <h2 className="text-2xl font-bold">{isNormal ? 'MATER KONTROLLDATA' : 'MATER HANDELSEN'}</h2>

          <Card className="max-w-md mx-auto">
            <CardContent className="pt-4 text-sm text-center space-y-2">
              <p className="font-semibold">Gor ingenting.</p>
              {!isNormal && <p className="text-muted-foreground">Flytta dig bort fran sensorn och lat luften stabiliseras.</p>}
              <p className="text-amber-600 font-semibold text-xs">STOPPA INTE AN — MATER HANDELSEN</p>
            </CardContent>
          </Card>

          <div className="grid grid-cols-3 gap-2 max-w-sm mx-auto">
            {!isNormal && <div className="rounded-lg border p-2 text-center"><div className="text-[9px] text-muted-foreground">PEAK PM2.5</div><div className="text-xl font-bold font-mono text-red-600">{peakPm25?.toFixed?.(0) || '—'}</div></div>}
            {!isNormal && <div className="rounded-lg border p-2 text-center"><div className="text-[9px] text-muted-foreground">Δ BASELINE</div><div className="text-xl font-bold font-mono text-amber-600">+{delta?.toFixed?.(0) || '0'}</div></div>}
            <div className="rounded-lg border p-2 text-center"><div className="text-[9px] text-muted-foreground">TID SEDAN EVENT</div><div className="text-xl font-bold font-mono">{fmtTime(eventElapsed)}</div></div>
          </div>

          <LiveValues latest={liveStatus?.latest_reading} />
          <MiniChart data={chartData} markers={session?.event_markers || liveStatus?.event_markers} height={180} />

          <div className="text-xs text-muted-foreground">
            Minst {fmtTime(minPost)} kvar | {liveStatus?.readings_count || 0} matpunkter
          </div>

          <Button variant="outline" size="sm" onClick={forceStop} className="text-xs">
            Avsluta manuellt (ej rekommenderat)
          </Button>
        </div>
      </div>
    );
  }

  // ----- RECOVERY -----
  if (phase === 'recovery') {
    const rc = recoveryCheck;
    return (
      <div className={containerCls} data-testid="guided-recovery">
        <div className="text-center space-y-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Steg 3 av 4 — Aterhomtning</div>
          <h2 className="text-xl font-bold">VANTAR PA ATERHOMTNING</h2>

          <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
            <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">BASELINE PM2.5</div><div className="text-2xl font-bold font-mono text-emerald-600">{rc?.baseline_pm25 || '—'}</div></div>
            <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">NU</div><div className="text-2xl font-bold font-mono" style={{ color: rc?.recovered ? '#22c55e' : '#f59e0b' }}>{rc?.current_pm25 || '—'}</div></div>
            <div className="rounded-lg border p-3 text-center"><div className="text-[10px] text-muted-foreground">STATUS</div><div className="text-base font-bold">{rc?.recovered ? 'Aterstolld' : 'Aterhamtar...'}</div></div>
          </div>

          <LiveValues latest={liveStatus?.latest_reading} />
          <MiniChart data={chartData} markers={liveStatus?.event_markers} height={180} />

          <div className="text-xs text-muted-foreground">
            Tid sedan event: {fmtTime(eventElapsed)} | Matpunkter: {liveStatus?.readings_count || 0}
          </div>
        </div>
      </div>
    );
  }

  // ----- COMPLETING -----
  if (phase === 'completing') {
    return (
      <div className={containerCls + ' flex items-center justify-center'}>
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 mx-auto animate-spin text-emerald-500" />
          <h2 className="text-xl font-bold">Avslutar testet...</h2>
        </div>
      </div>
    );
  }

  // ----- GROUND TRUTH -----
  if (phase === 'ground_truth') {
    const cd = completionData;
    const noChange = cd?.computed?.pm25_delta != null && Math.abs(cd.computed.pm25_delta) < 3;
    return (
      <div className={containerCls} data-testid="guided-ground-truth">
        <div className="text-center space-y-4">
          <div className="text-xs text-muted-foreground uppercase tracking-widest">Steg 4 av 4</div>
          <h2 className="text-2xl font-bold">TEST KLART</h2>

          {cd && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-w-lg mx-auto text-xs">
              <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-950/20 p-2 text-center">
                <CheckCircle className="h-4 w-4 mx-auto text-emerald-500 mb-0.5" />
                {cd.readings_count} matpunkter sparade
              </div>
              <div className={`rounded-lg border p-2 text-center ${cd.computed?.baseline_status === 'missing' ? 'bg-amber-50 dark:bg-amber-950/20' : 'bg-emerald-50 dark:bg-emerald-950/20'}`} data-testid="gt-baseline-status">
                {cd.computed?.baseline_status === 'missing'
                  ? <><AlertTriangle className="h-4 w-4 mx-auto text-amber-500 mb-0.5" />Baseline saknas</>
                  : <><CheckCircle className="h-4 w-4 mx-auto text-emerald-500 mb-0.5" />{cd.computed?.baseline_status === 'unstable' ? 'Baseline (ej stabil)' : 'Baseline sparad'}</>}
              </div>
              <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-950/20 p-2 text-center">
                <CheckCircle className="h-4 w-4 mx-auto text-emerald-500 mb-0.5" />
                Event markerat
              </div>
              <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-950/20 p-2 text-center">
                <CheckCircle className="h-4 w-4 mx-auto text-emerald-500 mb-0.5" />
                Kvalitet: {cd.data_quality?.status || '?'}
              </div>
            </div>
          )}

          {noChange && spec?.category !== 'normal' && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 max-w-md mx-auto text-sm">
              <AlertTriangle className="h-5 w-5 text-amber-600 mx-auto mb-1" />
              <p className="font-semibold text-amber-700">Ingen matbar forandring</p>
              <p className="text-xs text-amber-600">Vi sag ingen tydlig forandring efter handelsen. Vad hande?</p>
            </div>
          )}

          <h3 className="text-lg font-bold pt-4">VAD UTFORDE DU?</h3>
          <div className="grid grid-cols-2 gap-2 max-w-sm mx-auto">
            {GT_OPTIONS.map(opt => (
              <Button key={opt.value} variant={selectedGT === opt.value ? 'default' : 'outline'}
                className="h-14 text-base font-bold"
                style={selectedGT === opt.value ? { backgroundColor: opt.color, borderColor: opt.color } : { borderColor: opt.color + '66', color: opt.color }}
                onClick={() => setSelectedGT(opt.value)} data-testid={`gt-${opt.value}`}>
                {opt.label}
              </Button>
            ))}
          </div>

          <Button className="h-12 px-10 text-base font-bold mt-4" disabled={!selectedGT}
            onClick={confirmGroundTruth} data-testid="confirm-gt-btn">
            BEKRAFTA <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ----- RESULTS -----
  if (phase === 'results') {
    const cd = completionData;
    const comp = cd?.computed || {};
    const bl = comp.baseline;
    const ar = cd?.alarm_result;
    const gtLabel = GT_OPTIONS.find(o => o.value === selectedGT);
    return (
      <div className={containerCls} data-testid="guided-results">
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-center">TESTRESULTAT</h2>

          {/* Ground truth badge */}
          <div className="text-center">
            <Badge className="text-base px-4 py-1" style={{ backgroundColor: gtLabel?.color, color: 'white' }}>
              Ground truth: {gtLabel?.label}
            </Badge>
          </div>

          {/* Baseline vs Peak */}
          {comp.baseline_status && comp.baseline_status !== 'stable' && (
            <p className="text-xs text-center text-amber-600" data-testid="results-baseline-note">
              {comp.baseline_status === 'missing'
                ? 'Baseline saknas – det fanns inga giltiga mätpunkter före händelsen. Delta kan inte beräknas.'
                : 'Baseline beräknad från mätpunkter före händelsen men var inte stabil.'}
            </p>
          )}
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Baseline</div>
              <div className="rounded-lg border p-2 space-y-1 text-xs">
                <div>PM1: <strong className="font-mono">{bl?.pm1 ?? 'saknas'}</strong></div>
                <div>PM2.5: <strong className="font-mono">{bl?.pm2_5 ?? 'saknas'}</strong></div>
                <div>PM10: <strong className="font-mono">{bl?.pm10 ?? 'saknas'}</strong></div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Peak</div>
              <div className="rounded-lg border p-2 space-y-1 text-xs">
                <div>PM1: <strong className="font-mono">{comp.peak_pm1?.toFixed?.(1) ?? '—'}</strong></div>
                <div>PM2.5: <strong className="font-mono">{comp.peak_pm25?.toFixed?.(1) ?? '—'}</strong></div>
                <div>PM10: <strong className="font-mono">{comp.peak_pm10?.toFixed?.(1) ?? '—'}</strong></div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase mb-1">Delta</div>
              <div className="rounded-lg border p-2 space-y-1 text-xs">
                <div>PM1: <strong className="font-mono text-red-600">+{comp.pm1_delta?.toFixed?.(1) ?? '—'}</strong></div>
                <div>PM2.5: <strong className="font-mono text-red-600">+{comp.pm25_delta?.toFixed?.(1) ?? '—'}</strong></div>
                <div>PM10: <strong className="font-mono text-red-600">+{comp.pm10_delta?.toFixed?.(1) ?? '—'}</strong></div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg border p-2"><span className="text-muted-foreground">Matpunkter:</span> <strong>{cd?.readings_count}</strong></div>
            <div className="rounded-lg border p-2"><span className="text-muted-foreground">Duration:</span> <strong>{comp.duration_s}s</strong></div>
            <div className="rounded-lg border p-2"><span className="text-muted-foreground">PM1/PM2.5:</span> <strong>{comp.pm1_pm25_ratio}</strong></div>
            <div className="rounded-lg border p-2"><span className="text-muted-foreground">Kvalitet:</span> <strong>{cd?.data_quality?.status}</strong></div>
          </div>

          {cd?.live_result && (
            <div className="rounded-lg border p-3 text-xs" data-testid="results-live-decision">
              <div className="font-semibold text-sm">
                Riktiga systemets beslut: {cd.live_result.classification === 'vape' ? 'VAPE' : cd.live_result.classification === 'suspect' ? 'MISSTÄNKT' : 'NORMAL'} (max {Math.round((cd.live_result.peak_score || 0) * 100)} %)
              </div>
              <div className="text-muted-foreground mt-1">
                {cd.live_result.decisions} beslut loggade · {cd.live_result.positive_readings} positiva · {cd.live_result.spray_filtered_readings} sprayfiltrerade
                {cd.live_result.seconds_to_detection != null && ` · första detektion ca ${cd.live_result.seconds_to_detection} s efter markör (ungefärligt)`}
              </div>
            </div>
          )}

          {ar && (
            <div className={`rounded-lg border p-3 text-xs ${ar.alarm_triggered ? 'bg-red-50 border-red-200 dark:bg-red-950/20' : 'bg-slate-50 dark:bg-slate-900'}`} data-testid="results-alarm">
              <div className="font-semibold text-sm">
                {ar.alarm_triggered
                  ? `Larm utlöstes: ${ar.first_event?.type === 'vape' ? 'VAPE' : 'MISSTÄNKT'} (${Math.round((ar.first_event?.score || 0) * 100)} %)`
                  : ar.merged_into_open_event ? 'Inget nytt larm – ett redan öppet larm på sensorn uppdaterades' : 'Inget larm utlöstes'}
              </div>
              {ar.seconds_to_alarm != null && (
                <div className="mt-1" data-testid="results-time-to-alarm">
                  Tid till larm: ca {ar.seconds_to_alarm} s <span className="text-muted-foreground">(uppskattad från händelsemarkör, ej exakt exponeringstid{ar.reading_interval_s ? `; mätintervall ca ${ar.reading_interval_s} s` : ''})</span>
                </div>
              )}
            </div>
          )}

          {/* Chart */}
          <MiniChart data={chartData} markers={liveStatus?.event_markers || session?.event_markers} height={200} />

          <Button className="w-full h-12 text-base font-bold" onClick={goToNextTest} data-testid="next-test-btn">
            NASTA TEST <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // ----- NEXT TEST -----
  if (phase === 'next_test') {
    const rec = recommendation;
    const ready = roomStatus?.ready;
    return (
      <div className={containerCls} data-testid="guided-next-test">
        <div className="space-y-4">
          {!ready && (
            <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/10">
              <CardContent className="pt-4 text-center space-y-2">
                <Loader2 className="h-6 w-6 mx-auto animate-spin text-amber-500" />
                <p className="text-sm font-semibold">Kontrollerar luften...</p>
                <p className="text-xs text-muted-foreground">PM2.5: {roomStatus?.current_pm25 ?? '—'} (mal: &lt;{roomStatus?.max_threshold ?? 10})</p>
                <Button variant="outline" size="sm" onClick={async () => {
                  const r = await fetch(`${API}/api/test-lab/room-status/${deviceId}`, { headers: h });
                  if (r.ok) setRoomStatus(await r.json());
                }}>Kontrollera igen</Button>
              </CardContent>
            </Card>
          )}

          {ready && (
            <div className="text-center py-2">
              <Badge className="bg-emerald-100 text-emerald-700 text-sm px-4 py-1">
                <CheckCircle className="h-4 w-4 mr-1" /> RUMMET REDO FOR NASTA TEST
              </Badge>
            </div>
          )}

          {rec && !rec.done && (
            <Card className="border-2 border-blue-200 bg-blue-50/30 dark:bg-blue-950/10">
              <CardContent className="pt-4 space-y-3">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Nasta rekommenderade test</div>
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-lg"
                    style={{ backgroundColor: rec.spec.category === 'vape' ? '#ef4444' : rec.spec.category === 'spray' ? '#f59e0b' : '#a855f7' }}>
                    {rec.spec.category?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-lg">{rec.spec.label}</div>
                    <div className="text-sm text-muted-foreground">{rec.reason}</div>
                  </div>
                </div>
                <Button className="w-full h-12 text-base font-bold" onClick={() => startGuided()}
                  disabled={!ready} data-testid="start-next-btn">
                  <Play className="h-5 w-5 mr-2" /> STARTA NASTA TEST
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onExit}>Paus</Button>
            <Button variant="outline" className="flex-1" onClick={() => { setPhase('start'); }}>Valj annat test</Button>
          </div>

          {/* Progress */}
          {coverage && (
            <Card>
              <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Progress</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span>{coverage.total_completed} / {coverage.total_target}</span>
                  <span className="font-mono">{coverage.progress_pct}%</span>
                </div>
                <Progress value={coverage.progress_pct} className="h-2" />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Fallback
  return <div className="text-center py-8"><Button onClick={onExit}>Tillbaka</Button></div>;
}
